import { Button, Uploader, UploadStatus } from "@lyfegen-platform/react-common";
import { format } from "date-fns";
import saveAs from "file-saver";
import { FlexContainerType as FContainerType } from "libs/react-common/src/types/graphqlTypes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { FlexContainerType, FlexEntityUploadReport } from "../../../../models/generated/graphql";
import { userOrganization } from "../../../../store/layout";
import { bulk } from "../../../../store/upload";
import getReportFileData from "./get-report-txt";

const BulkRoot = () => {
	const { t } = useTranslation();
	const [{ status, report, progress, isDisplayed, casesCreated, patientsCreated, formsCreated }, setBulkDetails] = useRecoilState(bulk);
	const { name: userOrgName } = useRecoilValue(userOrganization);
	const fileName = `Bulk report for ${userOrgName} on ${format(new Date(), "yyyy-MM-dd")}.txt`;
	const fileType = "text/plain;charset=utf-8";

	useEffect(() => {
		if (status && UploadStatus.GeneratingForms == status && formsCreated?.length && progress !== 100) {
			setBulkDetails({
				isDisplayed: true,
				status: UploadStatus.AgreementExecution,
				report: {
					submittedEntities: (formsCreated || []).filter(Boolean).map((patientId) => ({ patientId: patientId! })),
					patientsCreated: patientsCreated || [],
					casesCreated: casesCreated || [],
					createdOn: new Date(),
					completedIn: 0,
					responseType: FContainerType.Form,
					percentageSourceFile: progress,
					failedToCreate: [],
					revokedEntities: [],
					failedToRevoke: [],
				} as FlexEntityUploadReport,
				type: FlexContainerType.Form,
			});
		}

		if (status && [UploadStatus.AgreementExecution, UploadStatus.GeneratingForms].includes(status) && progress === 100) {
			setBulkDetails({
				isDisplayed: true,
				status: UploadStatus.FinalReport,
				report: {
					submittedEntities: (formsCreated || []).filter(Boolean).map((patientId) => ({ patientId: patientId! })),
					patientsCreated: patientsCreated || [],
					casesCreated: casesCreated || [],
					createdOn: new Date(),
					completedIn: 0,
					responseType: FContainerType.Form,
					percentageSourceFile: progress,
					failedToCreate: [],
					revokedEntities: [],
					failedToRevoke: [],
				} as FlexEntityUploadReport,
				type: FlexContainerType.Form,
			});
		}
	}, [progress, casesCreated, formsCreated]);

	const handleGetFinalReport = () => {
		const reportTxtProps = {
			report,
			userOrgName,
			t,
			formType: "form-type-COVERAGE_REQUEST",
			isPreliminary: false,
			uploadType: FlexContainerType.Form,
			duplicatesFromFile: 0,
			totalNumberOfRecords: 0,
		};
		const data = new Blob([getReportFileData({ ...reportTxtProps, isPreliminary: false, isBulk: true })], { type: fileType });
		saveAs(data, fileName);
	};

	const getComponentButtons = (): Array<JSX.Element> => {
		if (status === UploadStatus.FinalReport) {
			return [
				<Button color="primary" use="secondary" onClick={handleGetFinalReport}>
					{t("get-report")}
				</Button>,
			];
		}

		return [];
	};

	if (!isDisplayed || (status === UploadStatus.AgreementExecution && progress !== 100)) return <></>;

	const handleClose = () => {
		setBulkDetails({ isDisplayed: false, type: FlexContainerType.Form });
	};

	return (
		<Uploader
			status={status}
			report={report}
			progress={progress}
			cancel={handleClose}
			t={t}
			buttons={getComponentButtons()}
			type={FContainerType.Form}
		/>
	);
};

export default BulkRoot;
